import { useState, useRef, useMemo, useCallback, useLayoutEffect, useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import { withData } from './DataController.js';
import { getEditableStyles, getTableStyles } from './components/Shared/EditableUtils.js';
import { ReactEditor } from 'slate-react';
import { Node as SlateNode, Path } from 'slate';
import { toastSuccess } from './components/popups/Toast.js';
import { withRemote } from './Utils/Utils.js';
import './sheets-of-paper.css';
import './sheets-of-paper-a4.css';
import './FlowingPage.scss';
import { pxlsToPts, getBaseHeightInPts } from './FlowingPage/pageLayoutMath';
import { computeAutoPageBreaks } from './FlowingPage/pageBreakEngine';
import { mouseDownReducer, mouseMoveReducer, mouseUpReducer } from './FlowingPage/pageBoundaryInteraction';
import { renderFlowingChildren } from './FlowingPage/ChildrenRenderer';
import { runFootnoteOrchestrator } from './FlowingPage/footnoteEngine/footnoteOrchestrator';
import { runFootnoteOrchestratorV2 } from './FlowingPage/footnoteEngine/footnoteOrchestratorV2';
import {
	removePageBreakEntity as _removePageBreakEntity,
	snapPageBreak as _snapPageBreak,
} from './FlowingPage/pageBreakSnapping';
import { cleanupStalePageBreaks } from './FlowingPage/stalePageBreakCleanup';
import { buildEditorAddins } from './FlowingPage/buildEditorAddins';
import PageBoundary from './FlowingPage/PageBoundary';
import { useFlowingContent } from './FlowingPage/useFlowingContent';
import Editor from './contents/Editor.js';

function computeEditorHeight({
	pages,
	baseHeight,
	firstPageBaseHeight,
	lastPageBaseHeight,
	FirstAndlastPageBaseHeight,
	lastPageMargins,
	orderedBoundaries,
	lastBoundary,
}) {
	if (FirstAndlastPageBaseHeight && pages === 1) return FirstAndlastPageBaseHeight;
	if (firstPageBaseHeight && pages === 1) return firstPageBaseHeight + lastBoundary;
	if (lastPageBaseHeight && pages === 1) return lastPageBaseHeight;
	if (lastPageMargins) {
		return orderedBoundaries.length > 0 && orderedBoundaries[orderedBoundaries.length - 1].pos + lastPageBaseHeight;
	}
	return baseHeight + lastBoundary;
}

function FlowingPage(props) {

	// ---------------------------------------------------------------------------
	// Content state (from listener / data-loading hook)
	// ---------------------------------------------------------------------------
	const {
		content, setContent,
		pageBreaks, setPageBreaks,
		manualPageBreaks, setManualPageBreaks,
		pages, setPages,
		editorKey,
	} = useFlowingContent(props.data, props.flowingConfig);

	// ---------------------------------------------------------------------------
	// UI state
	// ---------------------------------------------------------------------------
	const [editor, setEditor] = useState(null);
	const [mouseDown, _setMouseDown] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const mouseDownRef = useRef(null);
	const setMouseDown = (val) => {
		mouseDownRef.current = val;
		_setMouseDown(val);
		setIsDragging(val !== null);
	};
	const [toolbar, setToolbar] = useState(
		props.flowingConfig.customisedToolbarButtons
			? { type: "customised", buttons: props.flowingConfig.customisedToolbarButtons }
			: { type: "full" }
	);
	const [keepInsertOpen, setKeepInsertOpen] = useState(false);

	// contentVersion increments each time onChange fires, triggering the
	// orchestrator via useLayoutEffect. It does NOT track actual content value.
	const [contentVersion, setContentVersion] = useState(0);

	// ---------------------------------------------------------------------------
	// Refs (don't trigger re-renders)
	// ---------------------------------------------------------------------------
	const divRef = useRef(null);
	const tableStyleRef = useRef(null);
	const dataRef = useRef(null);          // was this.data
	const heightRef = useRef(null);        // was this.height
	const shallowOnChangeCount = useRef(0);  // useRef: no re-render, sync mutations
	const pendingOrchestratorRef = useRef(null);
	const savedScrollYRef = useRef(null);
	const dragElementRef = useRef(null);   // DOM node of the boundary being dragged
	const pageBreaksRef = useRef(pageBreaks);
	const manualPageBreaksRef = useRef(manualPageBreaks);

	// ---------------------------------------------------------------------------
	// Memoised styles
	// ---------------------------------------------------------------------------
	const editableStyles = useMemo(
		() => getEditableStyles(props.styles, props.flowingConfig.styles, props.flowingConfig.tableStyles, props.data),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.styles, props.flowingConfig, props.data]
	);
	const tableStyles = useMemo(
		() => getTableStyles(props.styles, props.flowingConfig.rowStyleOptions, props.flowingConfig.columnStyleOptions, props.data),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.styles, props.flowingConfig, props.data]
	);

	// ---------------------------------------------------------------------------
	// setState shim
	// Orchestrator and buildEditorAddins call a setState-like API.
	// shallowOnChangeCount lives in a ref — update is synchronous.
	// All other fields dispatch to individual useState setters.
	// Callbacks are called synchronously (ref updates are immediate).
	// ---------------------------------------------------------------------------
	function setStateBatch(update, callback) {
		if ('shallowOnChangeCount' in update) shallowOnChangeCount.current = update.shallowOnChangeCount;
		if ('editor' in update) setEditor(update.editor);
		if ('keepInsertOpen' in update) setKeepInsertOpen(update.keepInsertOpen);
		if ('manualPageBreaks' in update) setManualPageBreaks(update.manualPageBreaks);
		if ('mouseDown' in update) setMouseDown(update.mouseDown);
		if ('pageBreaks' in update) setPageBreaks(update.pageBreaks);
		if ('content' in update) setContent(update.content);
		if ('pages' in update) setPages(update.pages);
		if ('toolbar' in update) setToolbar(update.toolbar);
		if (callback) callback();
	}

	// ---------------------------------------------------------------------------
	// keyDown
	// Refs to track which structural key triggered the current onChange, so the
	// scroll-preservation logic in onChange knows whether to save the position.
	// ---------------------------------------------------------------------------
	const isEnterKeyRef = useRef(false);
	const isBackspaceKeyRef = useRef(false);
	const isDeleteKeyRef = useRef(false);
	useEffect(() => {
		const container = divRef.current;
		if (!container) return;

		const handleKeyDown = (e) => {
			if (e.key === 'Enter') {
				isEnterKeyRef.current = true;
			} else if (e.key === 'Backspace') {
				isBackspaceKeyRef.current = true;
			} else if (e.key === 'Delete') {
				isDeleteKeyRef.current = true;
			}
		};

		container.addEventListener('keydown', handleKeyDown, true);
		return () => container.removeEventListener('keydown', handleKeyDown, true);
	}, []);

	// ---------------------------------------------------------------------------
	// save (debounced 1500ms)
	// Uses ref pattern so the debounced wrapper always calls the latest impl.
	// ---------------------------------------------------------------------------
	const saveRef = useRef(null);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const save = useMemo(() => debounce(1500, (...args) => saveRef.current(...args)), []);
	saveRef.current = (data) => {
		const dataName = props.flowingConfig.dataName;
		props.data.updateLocalData(dataName, JSON.stringify(data));
		setTimeout(function (d, dn) {
			// Read from refs (not closure) so we serialise the latest values,
			// even if heightChange has updated the arrays since save was queued.
			const allBoundaries = pageBreaksRef.current.concat(manualPageBreaksRef.current);
			d.updateLocalData(dn + 'PageBreaks', JSON.stringify(allBoundaries, (k, v) => { if (k === 'ref') { return undefined; } return v; }));
		}, 1500, props.data, dataName);
	};

	// ---------------------------------------------------------------------------
	// onChange
	// Delegates to the footnote orchestrator via useLayoutEffect after DOM update.
	// ---------------------------------------------------------------------------
	const onChangeFnRef = useRef(null);
	onChangeFnRef.current = (data, isTransient, passedBoundaries = null) => {
		if (!data) {
			console.log("Not saving because no data.", data, isTransient);
			return;
		}
		dataRef.current = data;
		save(data);
		if (!isTransient) {
			// Save scroll position here — before any React re-render or
			// slate-react useLayoutEffect (scrollSelectionIntoView) can move it.
			if ((isEnterKeyRef.current || isBackspaceKeyRef.current || isDeleteKeyRef.current) && savedScrollYRef.current === null) {
				const anchorNode = editor.selection ? SlateNode.get(editor, editor.selection.anchor.path) : null;
				const parentPath = anchorNode ? Path.parent(editor.selection.anchor.path) : null;
				const parentNode = parentPath ? SlateNode.get(editor, parentPath) : null;
				if (parentNode && (parentNode.type === 'table-footer' || parentNode.type === 'footnote-content')) {
					const scrollContainer = document.getElementById('builder-page-page-wrapper');
					savedScrollYRef.current = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
				}
				isEnterKeyRef.current = false;
				isBackspaceKeyRef.current = false;
				isDeleteKeyRef.current = false;
			}
			pendingOrchestratorRef.current = { passedBoundaries };
			setContent(data);
			setContentVersion(v => v + 1);
		}
	};
	// Stable wrapper so Editor / orchestrator always call the latest impl
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onChange = useMemo(() => (...args) => onChangeFnRef.current(...args), []);

	// ---------------------------------------------------------------------------
	// Orchestrator — fires after every onChange-triggered DOM update
	// useLayoutEffect (not useEffect) matches the class's setState callback
	// timing: runs synchronously after DOM mutations, before paint.
	// ---------------------------------------------------------------------------
	useLayoutEffect(() => {
		if (!pendingOrchestratorRef.current || !editor || !divRef.current) return;
		const { passedBoundaries } = pendingOrchestratorRef.current;
		pendingOrchestratorRef.current = null;

		runFootnoteOrchestrator(
			editor,
			divRef.current,
			{
				editor,
				manualPageBreaks,
				shallowOnChangeCount: shallowOnChangeCount.current,
			},
			props,
			passedBoundaries,
			{
				setState: setStateBatch,
				onChange,
				heightChange,
			}
		);

		requestAnimationFrame(() => {
			if (savedScrollYRef.current !== null) {
				const scrollContainer = document.getElementById('builder-page-page-wrapper');
				if (scrollContainer) scrollContainer.scrollTop = savedScrollYRef.current;
				else window.scrollTo(0, savedScrollYRef.current);
				savedScrollYRef.current = null;
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentVersion]);

	// ---------------------------------------------------------------------------
	// heightChange (debounced 500ms)
	// ---------------------------------------------------------------------------
	const heightChangeRef = useRef(null);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const heightChange = useMemo(() => debounce(500, (...args) => heightChangeRef.current(...args)), []);
	heightChangeRef.current = (editorHeight, stateParam, passedManuals) => {
		if (!editorHeight || !dataRef.current) return;
		heightRef.current = editorHeight;

		const margins = props.flowingConfig.margins;
		const firstPageMargins = props.flowingConfig.firstPageMargins;
		const lastPageMargins = props.flowingConfig.lastPageMargins;
		const size = props.pageSize;

		const baseHeightInPts = getBaseHeightInPts(margins, size);
		const firstAndLastPageHeightInPts = (firstPageMargins && lastPageMargins) ? getBaseHeightInPts({ top: firstPageMargins.top, bottom: lastPageMargins.bottom }, size) : null;
		const firstPageBaseHeightInPts = firstPageMargins ? getBaseHeightInPts(firstPageMargins, size) : null;
		const lastPageBaseHeightInPts = lastPageMargins ? getBaseHeightInPts(lastPageMargins, size) : null;

		const manual = passedManuals ? passedManuals : manualPageBreaks;

		const outerEl = divRef.current;
		if (!outerEl) {
			console.warn("Can't get outer editor element!");
			return;
		}
		const blocks = outerEl.querySelectorAll('[data-slate-node=element]');
		const offset = blocks.item(0).getBoundingClientRect().y;

		const allPageBreaks = editor.getAllPageBreaks(content.length);
		const updateManualBreaks = [];

		if (!mouseDownRef.current) {
			for (let breakEntity of allPageBreaks) {
				try {
					const domNode = ReactEditor.toDOMNode(editor, breakEntity[0]);
					let textNode = domNode.firstChild;
					while (textNode.nodeType === 1) {
						textNode = textNode.firstChild;
					}
					breakEntity[0].pageBreaks.forEach(storedBreak => {
						const index = manual.findIndex(b => b.pageBreakId === storedBreak.id);
						if (index < 0) {
							// Node has a pageBreaks entry with no matching state entry.
							// Remove it — it's an orphan from stale storage. Undo
							// restorations are handled by Slate's onChange which will
							// trigger a new heightChange after the editor tree updates.
							console.warn(`[heightChange] removing orphan from node: breakId=${storedBreak.id}, path=[${breakEntity[1]}], nodeType=${breakEntity[0].type}`);
							_removePageBreakEntity(editor, content, { pageBreakId: storedBreak.id });
							return;
						}
						if (storedBreak.offset === 0) {
							manual[index].pos = (domNode.getBoundingClientRect().y - offset) / 1.3333;
							manual[index].at = breakEntity[1];
							updateManualBreaks.push(manual[index]);
						} else {
							//TODO offset
							let dataSlateTextNode = domNode.firstChild;
							let totalCharacterOffset = 0;
							while (dataSlateTextNode && (totalCharacterOffset + dataSlateTextNode.textContent.length < storedBreak.offset)) {
								totalCharacterOffset += dataSlateTextNode.textContent.length;
								dataSlateTextNode = dataSlateTextNode.nextSibling;
								if (dataSlateTextNode == null) {
									//reached the end - we've probably had a split.
									console.error("Probably been a split, can't find offset " + storedBreak.offset, storedBreak);
									//TODO FIXME, actually handle moving it to the next!!
									_removePageBreakEntity(editor, content, { pageBreakId: storedBreak.id });
									return;
								}
							}
							let textNodeInner = dataSlateTextNode.firstChild.firstChild;
							while (textNodeInner.nodeType === 1) {
								textNodeInner = textNodeInner.firstChild;
							}
							const text = SlateNode.string(breakEntity[0]);
							let start = 0;
							let end = text.length;
							const range = document.createRange();
							const nodeText = textNodeInner.textContent;
							const rangeOffset = storedBreak.offset - totalCharacterOffset;
							if (rangeOffset + 1 >= nodeText.length) {
								range.setStart(textNodeInner, nodeText.length - 2);
								range.setEnd(textNodeInner, nodeText.length - 1);
							} else {
								range.setStart(textNodeInner, rangeOffset);
								range.setEnd(textNodeInner, rangeOffset + 1);
							}
							manual[index].pos = (range.getBoundingClientRect().y - offset) / 1.3333;
							manual[index].at = breakEntity[1];
							updateManualBreaks.push(manual[index]);
						}
					});
				} catch (e) {
					console.log("break doesn't match!", breakEntity, e);
					cleanupStalePageBreaks(editor, content, breakEntity[0], _removePageBreakEntity);
				}
			}
			setManualPageBreaks(updateManualBreaks);
		}	//end mousedown

		const editorHeightInPts = pxlsToPts(editorHeight);
		console.log(`[heightChange] editorHeight=${editorHeightInPts.toFixed(1)}pt, baseHeight=${baseHeightInPts}pt, firstPage=${firstPageBaseHeightInPts}pt, lastPage=${lastPageBaseHeightInPts}pt, manualBreaks=${manual.length}, re-adopted=${updateManualBreaks.length}`);

		const { autos, tempBoundaries, missingAt } = computeAutoPageBreaks({
			editorHeightInPts,
			manualPageBreaks: manual,
			snapPageBreakFn: (theBreak, forceNextBlock, man) =>
				_snapPageBreak(editor, content, divRef.current, theBreak, forceNextBlock, man),
			editorChildren: editor.children,
			baseHeightInPts,
			firstAndLastPageHeightInPts,
			firstPageBaseHeightInPts,
			lastPageBaseHeightInPts,
			firstPageMargins,
			lastPageMargins,
		});

		console.log(`[heightChange] result: ${autos.length} auto breaks, ${tempBoundaries.length} total boundaries, ${tempBoundaries.length + 1} pages`);
		autos.forEach((a, i) => {
			const prev = i > 0 ? autos[i - 1].pos : 0;
			const gap = a.pos - prev;
			const nodeType = a.at ? editor.children[a.at[0]]?.type : '?';
			console.log(`  auto[${i}]: pos=${a.pos.toFixed(1)}pt, at=[${a.at}], gap=${gap.toFixed(1)}pt, nodeType=${nodeType}`);
		});

		const fragment = [{ type: 'text', children: [{ text: '' }] }];
		const formattedContent = missingAt ? [...dataRef.current, ...fragment] : dataRef.current;
		// stateParam.editor is passed by the orchestrator's "finished" branch;
		// fall back to the closed-over editor if not provided.
		const editorForLength = (stateParam && stateParam.editor) ? stateParam.editor : editor;
		const formattedAutos = autos.map((a, index) => {
			if (index === autos.length - 1 && missingAt) {
				return { ...a, at: [editorForLength.children.length], offset: 0 };
			} else return a;
		});

		if (missingAt) {
			setPageBreaks(formattedAutos);
			setContent(dataRef.current);
			setPages(tempBoundaries.length + 1 || 1);
			save(formattedContent);
		} else {
			setPageBreaks(formattedAutos);
			setContent(dataRef.current);
			setPages(tempBoundaries.length + 1 || 1);
		}
	};

	// ---------------------------------------------------------------------------
	// Mouse / drag handlers
	// ---------------------------------------------------------------------------
	const boundaryMouseDownRef = useRef(null);
	boundaryMouseDownRef.current = (e, b) => {
		e.preventDefault(); // prevent browser text-selection drag
		const patch = mouseDownReducer({ mouseDown: mouseDownRef.current, manualPageBreaks, pageBreaks }, b, e.target.className, e.button);
		if (patch) {
			// Capture the page-boundary DOM element for direct manipulation during drag
			let el = e.target;
			while (el && !el.classList.contains('page-boundary')) el = el.parentElement;
			dragElementRef.current = el;
			if (el) el.classList.add('dragging');

			if ('mouseDown' in patch) setMouseDown(patch.mouseDown);
			if ('manualPageBreaks' in patch) setManualPageBreaks(patch.manualPageBreaks);
		}
	};
	// Stable callback reference — never changes between renders so PageBoundary
	// (wrapped in React.memo) does not re-render due to callback identity.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const boundaryMouseDown = useCallback((e, b) => boundaryMouseDownRef.current(e, b), []);

	const mouseUpImplRef = useRef(null);
	mouseUpImplRef.current = () => {
		const current = mouseDownRef.current;
		if (!current) return;

		// Read manualPageBreaks from the ref — during drag, only the ref was
		// updated (direct DOM manipulation, no React setState), so the closure
		// value may be stale.
		const latestManuals = manualPageBreaksRef.current;
		const result = mouseUpReducer({ mouseDown: current, manualPageBreaks: latestManuals, pageBreaks });
		if (!result) {
			console.log("Unable to find break ", current.id);
			if (dragElementRef.current) dragElementRef.current.classList.remove('dragging');
			dragElementRef.current = null;
			setMouseDown(null);
			return;
		}
		const { theBreak, onTopOfAnother, manualPageBreaks: updatedManuals } = result;

		_removePageBreakEntity(editor, content, theBreak);
		if (onTopOfAnother) {
			_removePageBreakEntity(editor, content, theBreak);
		} else {
			_snapPageBreak(editor, content, divRef.current, theBreak, false, true);
			editor.addPageBreak(theBreak, theBreak.at);
		}

		// Keep transition disabled through the React re-render + heightChange
		// cycle.  Re-enable only after the browser has painted the final
		// position, so intermediate positions don't animate into view.
		const dragEl = dragElementRef.current;
		dragElementRef.current = null;
		setMouseDown(null);
		setManualPageBreaks(updatedManuals);
		heightChange(heightRef.current, null, updatedManuals);
		onChange(dataRef.current);

		if (dragEl) {
			// Double rAF: first fires after React commits to the DOM,
			// second fires after the browser paints that commit.
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					dragEl.classList.remove('dragging');
				});
			});
		}
	};

	const mouseMoveHandlerRef = useRef(null);
	mouseMoveHandlerRef.current = (e) => {
		if (!mouseDownRef.current) return;
		if (!divRef.current) return;
		const refPts = pxlsToPts(e.clientY - divRef.current.getBoundingClientRect().top);

		// Update the data model (cheap in-place mutations, no React setState).
		// The reducer sets mouseDown.pos and the matching break's .pos.
		mouseMoveReducer({ mouseDown: mouseDownRef.current, manualPageBreaks: manualPageBreaksRef.current }, refPts);

		// Move the boundary element directly in the DOM — zero React re-renders.
		// React state is synced once on mouseUp.
		if (dragElementRef.current) {
			dragElementRef.current.style.top = refPts + "pt";
		}
	};

	// Attach mousemove/mouseup to document during drag so the drag continues
	// even when the cursor leaves the component boundary.
	// Keyed on isDragging (boolean) — not mouseDown (object) — so listeners
	// are added once when drag starts and removed once when it ends, rather
	// than being torn down and re-added on every mousemove spread.
	useEffect(() => {
		if (!isDragging) return;

		const onDocMouseMove = (e) => mouseMoveHandlerRef.current(e);
		const onDocMouseUp = () => mouseUpImplRef.current();

		document.addEventListener('mousemove', onDocMouseMove);
		document.addEventListener('mouseup', onDocMouseUp);
		return () => {
			document.removeEventListener('mousemove', onDocMouseMove);
			document.removeEventListener('mouseup', onDocMouseUp);
		};
	}, [isDragging]);

	const handleBoundaryRightClickRef = useRef(null);
	handleBoundaryRightClickRef.current = (e, boundary) => {
		if (!boundary.id) return;
		if (e.type === 'contextmenu') {
			e.preventDefault();
			const updatedManuals = [...manualPageBreaks];
			const index = updatedManuals.findIndex(val => val.id === boundary.id);
			const theBreak = updatedManuals.splice(index, 1)[0];
			_removePageBreakEntity(editor, content, theBreak);
			setManualPageBreaks(updatedManuals);
			heightChange(heightRef.current, null, updatedManuals);
			onChange(dataRef.current);
		}
	};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleBoundaryRightClick = useCallback((e, b) => handleBoundaryRightClickRef.current(e, b), []);

	// ---------------------------------------------------------------------------
	// Misc handlers
	// ---------------------------------------------------------------------------
	function throwToaster() {
		toastSuccess('Link Copied');
	}

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------
	const margins = props.flowingConfig.margins;
	const firstPageMargins = props.flowingConfig.firstPageMargins;
	const lastPageMargins = props.flowingConfig.lastPageMargins;
	const size = props.pageSize;
	const width = size.width - margins.left - margins.right;
	const baseHeight = size.height - margins.top - margins.bottom;

	const firstPageBaseHeight = firstPageMargins && (size.height - firstPageMargins.top - firstPageMargins.bottom);
	const lastPageBaseHeight = lastPageMargins && (size.height - lastPageMargins.top - lastPageMargins.bottom);
	const FirstAndlastPageBaseHeight = firstPageMargins && lastPageMargins && (size.height - firstPageMargins.top - lastPageMargins.bottom);

	const allBoundaries = pageBreaks.concat(manualPageBreaks);
	const { allowFullWidthTable, extensions } = props.flowingConfig;
	const lastBoundary = allBoundaries.reduce((prev, curr) => curr.pos > prev ? curr.pos : prev, 0);

	const orderedBoundaries = [...allBoundaries].sort((a, b) => a.pos - b.pos);

	const height = computeEditorHeight({
		pages,
		baseHeight,
		firstPageBaseHeight,
		lastPageBaseHeight,
		FirstAndlastPageBaseHeight,
		lastPageMargins,
		orderedBoundaries,
		lastBoundary,
	});

	const pageBoundaries = allBoundaries.map((b, i) => (
		<PageBoundary
			key={"boundaryR" + i}
			boundary={b}
			margins={margins}
			size={size}
			onBoundaryMouseDown={boundaryMouseDown}
			onBoundaryContextMenu={handleBoundaryRightClick}
		/>
	));

	pageBreaksRef.current = pageBreaks;
	manualPageBreaksRef.current = manualPageBreaks;

	const { addins, options, myStyles, handleSlateExtensions } = buildEditorAddins(
		{
			editableStyles,
			tableStyles,
			tableStyleRef,
			allBoundaries,
			allowFullWidthTable,
		},
		props,
		{
			setState: setStateBatch,
			setToolbar,
			heightChange,
			height: heightRef.current,
			state: {
				pageBreaks,
				manualPageBreaks,
				pageBreaksRef,
				manualPageBreaksRef,
				content,
				keepInsertOpen,
				editor,
				divRef,
			},
		}
	);

	const styles = props.styles;
	const foundStyle = styles["link-colour-one"] || styles["brand-colour-one"] || styles["company-header"] || styles["page-header"] || styles["logo"] || null;
	const foundStyle2 = styles["link-colour-two"] || styles["brand-colour-two"] || null;
	const foundStyle3 = styles[props.flowingConfig.styles["p"]] || null;
	const colour1 = foundStyle && foundStyle.color ? foundStyle.color : null;
	const colour2 = foundStyle2 && foundStyle2.color ? foundStyle2.color : null;
	const colour3 = foundStyle3 && foundStyle3.color ? foundStyle3.color : null;
	const colourOptions = colour1 && colour2 && colour3 ? [colour1, colour2, colour3, '#2536f0'] : colour1 && colour2 ? [colour1, colour2, '#2536f0'] : colour1 ? [colour1, '#2536f0'] : ['#2536f0'];

	const slateExtensions = handleSlateExtensions(extensions);
	const customisedToolbarButtons = props.flowingConfig.customisedToolbarButtons ? { type: "customised", buttons: props.flowingConfig.customisedToolbarButtons } : null


	return (
		<div className={`page flowing-page${isDragging ? ' no-highlight' : ''}`} style={{ minHeight: props.pageSize.height + "pt" }}>
			{renderFlowingChildren({
				children: props.children,
				margins: props.flowingConfig.margins,
				firstPageMargins: props.flowingConfig.firstPageMargins,
				lastPageMargins: props.flowingConfig.lastPageMargins,
				size: props.pageSize,
				pages,
				lastBoundary,
			})}
			<div ref={divRef} style={{
				"position": "relative",
				"left": margins.left + "pt",
				"top": firstPageMargins ? firstPageMargins.top + "pt" : margins.top + "pt",
				"width": width + "pt",
				"paddingBottom": (margins.top + margins.bottom) + (firstPageMargins && pages > 1 ? firstPageMargins.top : 0) + "pt"
			}}>
				<Editor
					key={editorKey}
					readOnly={!props.data.localContentLoaded || props.uneditable}
					load={content}
					flowing={true}
					width={width}
					height={height}
					styles={props.flowingConfig.styles}
					myStyles={myStyles}
					onChange={onChange}
					heightChange={heightChange}
					allowFullWidthTable={allowFullWidthTable}
					options={options}
					entityMap={undefined}
					exchangeDataAs="editorState"
					addins={addins}
					toolbar={toolbar}
					setToolbar={setToolbar}
					colourOptions={colourOptions}
					throwToaster={throwToaster}
					renderFlowtingInsertMenu={Object.keys(slateExtensions).length > 0}
					extensions={slateExtensions}
					customisedToolbarButtons={customisedToolbarButtons}
					keepInsertOpen={keepInsertOpen}
					insertMenuPosition={{ marginLeft: 0, transform: `translateX(-${38 + margins.left}pt)` }}
					allBoundaries={allBoundaries}
				/>
				{pageBoundaries}
			</div>
		</div>
	);
}

export default withData(withRemote(FlowingPage));
